var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * External-identifier lookup subfield (CZNA-367).
 *
 * The `$a` text input stays directly editable — a cataloguer who already
 * knows the identifier can just type or paste it — and gains a search button
 * that queries the registry named by `sf.lookupSource` (ISNI / Wikidata /
 * ORCID) using MARC 100's surname/given name, then opens a picker modal over
 * the results. Both paths fill the sibling `$1` URI subfield (the first
 * sibling carrying a `uriTemplate`): picking a candidate uses its own URI
 * directly, manual typing/pasting falls blur-side to the same
 * `fillUriFromIdentifier` CZNA-196 uses in DynamicFieldPanel.
 *
 * Scoped to `personal.json` only (024-isni/024-wikidata/024-orcid), so the
 * MARC 100 path is hard-coded rather than threaded through as a prop — every
 * instance of this field lives on a form that also has exactly one 100.
 */
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { IconButton } from '@folio/stripes/components';
import { Controller } from 'react-hook-form';
import { resolveLabel } from '../renderer/labelResolver';
import { subfieldRowStyle, subfieldCodeStyle, subfieldLabelStyle, subfieldInputStyle, subfieldActionsStyle, subfieldInputBoxStyle, subfieldInputAdornedStyle, subfieldInputAdornmentStyle, } from './subfieldStyles';
import ClearFieldButton from './ClearFieldButton';
import useExternalIdentifierLookup from '../../../hooks/useExternalIdentifierLookup';
import ExternalIdentifierPickerModal from './ExternalIdentifierPickerModal';
import fillUriFromIdentifier from '../utils/fillUriFromIdentifier';
import OpenAtSourceLink from './OpenAtSourceLink';
var ExternalIdentifierLookupSubfield = function (_a) {
    var sf = _a.sf, pathPrefix = _a.pathPrefix, control = _a.control, inputId = _a.inputId, getValues = _a.getValues, setValue = _a.setValue, siblingSubfields = _a.siblingSubfields, sourceLabel = _a.sourceLabel;
    var intl = useIntl();
    var _b = useState(false), modalOpen = _b[0], setModalOpen = _b[1];
    var _c = useExternalIdentifierLookup(), search = _c.search, isLoading = _c.isLoading, isError = _c.isError, data = _c.data, reset = _c.reset;
    var fieldPath = "".concat(pathPrefix, ".").concat(sf.code, ".value");
    var source = sf.lookupSource;
    // CZNA-1614: room for the "open at source" link only where the group has a template.
    var opensAtSource = siblingSubfields.some(function (s) { return s.code !== sf.code && !!s.uriTemplate; });
    // MARC 100 path is not statically resolvable from this generic renderer's
    // types — cast via unknown, matching the project's established pattern for
    // dynamic RHF paths (`watchAny` in DynamicFieldPanel).
    var getValuesAny = getValues;
    var handleSearch = function () {
        var familyName = getValuesAny('fields.100.0.a.components.FAMILY_NAME') || '';
        var givenName = getValuesAny('fields.100.0.a.components.GIVEN_NAME') || '';
        setModalOpen(true);
        search({ source: source, familyName: familyName, givenName: givenName });
    };
    var handlePick = function (identifier, uri) {
        setValue(fieldPath, identifier, { shouldDirty: true });
        // Same sibling-match rule as CZNA-196's `fillUriFromIdentifier`: the first
        // OTHER subfield in the group that declares a `uriTemplate`.
        var uriSibling = siblingSubfields.find(function (s) { return s.code !== sf.code && !!s.uriTemplate; });
        if (uriSibling) {
            var uriPath = "".concat(pathPrefix, ".").concat(uriSibling.code, ".value");
            setValue(uriPath, uri, { shouldDirty: true });
        }
        setModalOpen(false);
    };
    var handleClose = function () {
        setModalOpen(false);
        reset();
    };
    return (React.createElement(Controller, { name: fieldPath, control: control, render: function (_a) {
            var field = _a.field;
            return (React.createElement("div", { style: subfieldRowStyle },
                !sf.formOnly && React.createElement("span", { style: subfieldCodeStyle, "aria-hidden": "true" },
                    "$",
                    sf.code),
                React.createElement("label", { htmlFor: inputId, style: subfieldLabelStyle },
                    resolveLabel(sf, intl),
                    sf.required && React.createElement("span", { style: { color: '#d0021b', marginLeft: '2px' }, "aria-hidden": "true" }, "*")),
                React.createElement("span", { style: subfieldInputBoxStyle },
                    React.createElement("input", { id: inputId, type: "text", style: opensAtSource ? __assign(__assign({}, subfieldInputStyle), subfieldInputAdornedStyle) : subfieldInputStyle, value: field.value || '', onChange: function (e) { return field.onChange(e.target.value); }, onBlur: function () {
                            field.onBlur();
                            // Same CZNA-196 sibling-fill as the plain-input renderer, for a
                            // cataloguer who types/pastes the identifier instead of using
                            // the search picker (handlePick covers the picker path).
                            fillUriFromIdentifier(field.value || '', siblingSubfields, pathPrefix, getValues, setValue);
                        }, placeholder: sf.placeholder }),
                    opensAtSource && (React.createElement("span", { style: subfieldInputAdornmentStyle },
                        React.createElement(OpenAtSourceLink, { code: sf.code, identifier: field.value || '', siblingSubfields: siblingSubfields, sourceLabel: sourceLabel })))),
                React.createElement("span", { style: subfieldActionsStyle },
                    React.createElement(ClearFieldButton, { visible: !!field.value, onClear: function () {
                            field.onChange('');
                            // CZNA-1467: the URI is the identifier's other spelling — it goes too.
                            fillUriFromIdentifier('', siblingSubfields, pathPrefix, getValues, setValue);
                        } }),
                    React.createElement(IconButton, { icon: "search", onClick: handleSearch, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.externalIdentifierLookup.searchButton' }) })),
                React.createElement(ExternalIdentifierPickerModal, { open: modalOpen, onClose: handleClose, isLoading: isLoading, isError: isError, result: data, onSelect: handlePick })));
        } }));
};
export default ExternalIdentifierLookupSubfield;
